import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { companyInfo } from "../data/mock";
import { Phone, ArrowRight, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CTASection = ({ calcData }) => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", city: "", service: "", message: "", honeypot: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Auto-fill from calculator
  useEffect(() => {
    if (calcData) {
      const systemLabel = calcData.displayTonnage
        ? `${calcData.displayTonnage} - ${calcData.system}`
        : calcData.system || "";

      const calcInfo = calcData.calculatorType === "residential"
        ? `[Residential Calculator] Room: ${calcData.inputs?.roomLength}x${calcData.inputs?.roomWidth} ft, ${calcData.inputs?.roomType}, ${calcData.inputs?.people} people, ${calcData.inputs?.floor} floor. Calculated: ${calcData.displayTonnage}. Suggested: ${calcData.system}.`
        : `[Commercial Calculator] Area: ${calcData.inputs?.area} sq ft, Type: ${calcData.inputs?.usageType}, ${calcData.inputs?.occupancy} people, ${calcData.inputs?.glassFacade} glass. Calculated: ${calcData.displayTonnage}. Suggested: ${calcData.system}. Scale: ${calcData.scale || ""}.`;

      setFormData(prev => ({
        ...prev,
        service: calcData.calculatorType === "residential" ? "residential" : "commercial",
        message: calcInfo,
      }));

      setSubmitted(false);
      setError("");
    }
  }, [calcData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      setError("Please fill in your name and phone number.");
      return;
    }

    if (formData.phone.replace(/[^0-9]/g, "").length < 10) {
      setError("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        service_required: formData.service,
        message: formData.message,
        calculator_type: calcData?.calculatorType || "",
        calculator_inputs: calcData?.inputs || null,
        calculated_tonnage: calcData?.tonnage || null,
        suggested_system: calcData?.system || "",
        source_page: calcData ? "calculator" : "cta_form",
        honeypot: formData.honeypot,
      };

      await axios.post(`${API}/leads`, payload);

      setSubmitted(true);
      toast.success("Your inquiry has been submitted! We'll contact you shortly.");
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many submissions. Please try again in a few minutes.");
      } else {
        setError("Something went wrong. Please try again or call us directly.");
      }
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData({ name: "", phone: "", email: "", city: "", service: "", message: "", honeypot: "" });
    setError("");
  };

  return (
    <section id="cta" className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-brand-900" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-5 leading-tight">
              From a Single AC to a Complete HVAC Ecosystem —
              <span className="text-accent-400"> We've Got You Covered.</span>
            </h2>
            <p className="text-slate-400 text-base leading-relaxed mb-8">
              Whether you need a simple AC installation or a complex centralized HVAC system for your commercial space, our team is ready to deliver. Get in touch today for a free consultation.
            </p>

            <div className="space-y-4 mb-8">
              {[
                "Free site visit & assessment",
                "No-obligation detailed quotation",
                "Expert technical consultation",
                "Priority scheduling available",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{text}</span>
                </div>
              ))}
            </div>

            {/* Calculator auto-fill indicator */}
            {calcData && (
              <div className="bg-accent-500/10 border border-accent-500/20 rounded-xl p-4 mb-6">
                <p className="text-accent-300 text-sm font-medium flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Calculator data auto-filled! ({calcData.calculatorType} — {calcData.displayTonnage})
                </p>
              </div>
            )}

            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 hover:bg-white/10 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Call us directly</p>
                <p className="text-white font-semibold group-hover:text-accent-400 transition-colors">
                  {companyInfo.phone}
                </p>
              </div>
            </a>

            {/* Email Contact */}
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a href={`mailto:${companyInfo.email}`} className="text-slate-400 hover:text-accent-400 text-sm transition-colors">
                <span className="text-slate-500">Queries:</span> {companyInfo.email}
              </a>
              <a href={`mailto:${companyInfo.supportEmail}`} className="text-slate-400 hover:text-accent-400 text-sm transition-colors">
                <span className="text-slate-500">Support:</span> {companyInfo.supportEmail}
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-2xl p-7 lg:p-8 shadow-xl">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h3>
                <p className="text-slate-500 text-sm mb-6">We've received your inquiry. Our team will contact you within 24 hours.</p>
                <Button onClick={handleReset} variant="outline" className="text-sm rounded-lg">
                  Submit Another Inquiry
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-900 mb-1.5">Get a Free Consultation</h3>
                <p className="text-slate-500 text-sm mb-6">Fill in your details and we'll get back to you promptly.</p>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot - hidden from users */}
                  <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <input name="honeypot" value={formData.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1.5 block">Full Name *</label>
                      <Input name="name" value={formData.name} onChange={handleChange}
                        placeholder="Your name" className="h-11 rounded-lg border-slate-200 focus:border-brand-400 focus:ring-brand-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1.5 block">Phone Number *</label>
                      <Input name="phone" value={formData.phone} onChange={handleChange}
                        placeholder="+91-XXXXXXXXXX" className="h-11 rounded-lg border-slate-200 focus:border-brand-400 focus:ring-brand-400" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange}
                        placeholder="your@email.com" className="h-11 rounded-lg border-slate-200 focus:border-brand-400 focus:ring-brand-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1.5 block">City / Location</label>
                      <Input name="city" value={formData.city} onChange={handleChange}
                        placeholder="e.g. Delhi NCR" className="h-11 rounded-lg border-slate-200 focus:border-brand-400 focus:ring-brand-400" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Service Required</label>
                    <select name="service" value={formData.service} onChange={handleChange}
                      className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm text-slate-700 bg-white focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400">
                      <option value="">Select a service</option>
                      <option value="residential">Residential AC Service</option>
                      <option value="commercial">Commercial HVAC System</option>
                      <option value="vrf">VRF / VRV Installation</option>
                      <option value="central">Central Air Conditioning</option>
                      <option value="amc">Annual Maintenance Contract</option>
                      <option value="design">HVAC Design & Planning</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Message / Notes</label>
                    <Textarea name="message" value={formData.message} onChange={handleChange}
                      placeholder="Tell us about your requirements..." rows={3}
                      className="rounded-lg border-slate-200 focus:border-brand-400 focus:ring-brand-400 resize-none" />
                  </div>

                  <Button type="submit" disabled={submitting}
                    className="w-full bg-accent-500 hover:bg-accent-600 text-white h-11 rounded-lg text-sm font-medium shadow-md shadow-accent-200 transition-all duration-200 hover:shadow-lg hover:shadow-accent-300 disabled:opacity-70">
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <>Submit Inquiry <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Calculator icon import for the indicator
const Calculator = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="16" height="20" x="4" y="2" rx="2" />
    <line x1="8" x2="16" y1="6" y2="6" />
    <line x1="16" x2="16" y1="14" y2="18" />
    <path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" />
    <path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" />
  </svg>
);
